import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  CONTENT_LIMITS,
  CTA_TYPES,
  CTA_TYPE_LABELS,
  DEFAULT_SPONSORSHIP_LABEL,
  PROMOTION_IMAGE_MIME_TYPES,
  SPONSORED_IMAGE_MAX_BYTES,
  SPONSORED_IMAGE_MAX_EDGE_PX,
  canPerform,
  type CtaType,
  type TargetScope,
} from "@vital/shared";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { MetricCard } from "../components/MetricCard";
import { InspectPanel } from "../components/InspectPanel";
import { SponsoredPreview } from "../components/SponsoredPreview";
import { PasswordConfirmModal } from "../components/PasswordConfirmModal";
import { TargetFields, targetingPayload, formatTargetLabel } from "../components/TargetFields";
import {
  ScheduleFields,
  schedulePayloadFromLocal,
  localScheduleFromIso,
  formatKabulDisplay,
} from "../components/ScheduleFields";
import {
  SponsoredAdvertiserList,
  SponsoredFilterBar,
  SponsoredInspectSummary,
  SponsoredInventoryList,
} from "../components/sponsored-inventory";
import { OpsEmptyState, SectionHeader } from "../components/ops-display";
import {
  ComposerHeader,
  ComposerPanel,
  ComposerSection,
  ComposerWorkspace,
} from "../components/ComposerChrome";
import { StickyFormActions } from "../components/StickyFormActions";
import { cmsStrings } from "../terminology";
import { useAuth } from "../context/AuthContext";
import { api, ApiError } from "../lib/api";
import { useInspectSearchParam } from "../lib/use-inspect-search-param";
import { FILTER_ALL } from "../lib/content-state";
import {
  filterAndSortSponsoredCampaigns,
  sponsoredCtaLabelFa,
  sponsoredCreativeFilename,
  sponsoredSponsorshipLabelOptions,
  type SponsoredSortKey,
} from "../lib/sponsored-ops";

const HEADLINE_MAX = 80;
const DESCRIPTION_MAX = 160;
const OFFER_MAX = 40;
const LABEL_MAX = 40;
const NAME_MAX = 120;
const ADVERTISER_NAME_MAX = 80;
const NOTES_MAX = 500;

type Site = { id: string; code: string; name: string };
type SiteGroup = {
  id: string;
  name: string;
  members: Array<{ siteId: string; siteCode: string; siteName: string }>;
};

type Advertiser = {
  id: string;
  displayName: string;
  internalNotes: string | null;
  status: "active" | "archived";
};

type Campaign = {
  id: string;
  advertiserId: string;
  advertiserName: string;
  advertiserStatus: string;
  name: string;
  scope: string;
  priority: number;
  state: string;
  pausedFromState: string | null;
  startsAt: string;
  endsAt: string;
  rejectionReason: string | null;
  submittedAt: string | null;
  headline: string;
  description: string;
  offerText: string | null;
  sponsorshipLabel: string;
  imagePath: string;
  imageAlt: string;
  ctaType: CtaType | null;
  ctaLabel: string;
  ctaHref: string;
  siteCode: string | null;
  siteName: string | null;
  groupId: string | null;
  groupName: string | null;
  createdAt: string;
  updatedAt: string;
};

type Snapshot = {
  id: string;
  version: number;
  publishedAt: string;
  payloadChecksum: string;
  rollbackOfSnapshotId: string | null;
};

type PasswordMode = "publish" | "archive" | "pause" | "unpause";


export function SponsoredPage() {
  const { user } = useAuth();
  const authInput = {
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role ?? null,
  };
  const canEdit = canPerform({ ...authInput, permission: "content.edit" });
  const canPublish = canPerform({ ...authInput, permission: "content.publish" });

  const [sites, setSites] = useState<Site[]>([]);
  const [groups, setGroups] = useState<SiteGroup[]>([]);
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [approvedUrls, setApprovedUrls] = useState<string[]>([]);
  const [sponsoredAllowed, setSponsoredAllowed] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [listFilter, setListFilter] = useState<string>(FILTER_ALL);
  const [listQuery, setListQuery] = useState("");
  const [listSort, setListSort] = useState<SponsoredSortKey>("updated");
  const [selectedId, setSelectedId] = useInspectSearchParam();

  const [advertiserName, setAdvertiserName] = useState("");
  const [advertiserNotes, setAdvertiserNotes] = useState("");
  const [savingAdvertiser, setSavingAdvertiser] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [advertiserId, setAdvertiserId] = useState("");
  const [name, setName] = useState("");
  const [headline, setHeadline] = useState("");
  const [description, setDescription] = useState("");
  const [offerText, setOfferText] = useState("");
  const [sponsorshipLabel, setSponsorshipLabel] = useState(DEFAULT_SPONSORSHIP_LABEL);
  const [siteCode, setSiteCode] = useState("herat-firdoosi-01");
  const [targetScope, setTargetScope] = useState<TargetScope>("site");
  const [groupId, setGroupId] = useState("");
  const [priority, setPriority] = useState(0);
  const [ctaType, setCtaType] = useState<CtaType>("whatsapp");
  const [ctaLabel, setCtaLabel] = useState("");
  const [customApprovedUrl, setCustomApprovedUrl] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [startsAtLocal, setStartsAtLocal] = useState("");
  const [endsAtLocal, setEndsAtLocal] = useState("");
  const [saving, setSaving] = useState(false);

  const [passwordModal, setPasswordModal] = useState<{
    mode: PasswordMode;
    campaign: Campaign;
  } | null>(null);
  const [password, setPassword] = useState("");
  const [actionPending, setActionPending] = useState(false);

  const [rejectModal, setRejectModal] = useState<Campaign | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const [historyTarget, setHistoryTarget] = useState<Campaign | null>(null);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<Snapshot | null>(null);
  const [rollbackPassword, setRollbackPassword] = useState("");
  const [rollbackBusy, setRollbackBusy] = useState(false);

  const activeAdvertisers = useMemo(
    () => advertisers.filter((a) => a.status === "active"),
    [advertisers],
  );

  const resetForm = () => {
    setEditingId(null);
    setAdvertiserId(activeAdvertisers[0]?.id ?? "");
    setName("");
    setHeadline("");
    setDescription("");
    setOfferText("");
    setSponsorshipLabel(DEFAULT_SPONSORSHIP_LABEL);
    setCtaType("whatsapp");
    setCtaLabel("");
    setCustomApprovedUrl("");
    setImagePath("");
    setImageAlt("");
    setImagePreviewUrl(null);
    setSiteCode(sites[0]?.code ?? "herat-firdoosi-01");
    setTargetScope("site");
    setGroupId("");
    setPriority(0);
    setStartsAtLocal("");
    setEndsAtLocal("");
  };

  const loadFormFromCampaign = (c: Campaign) => {
    setEditingId(c.id);
    setAdvertiserId(c.advertiserId);
    setName(c.name);
    setHeadline(c.headline);
    setDescription(c.description);
    setOfferText(c.offerText ?? "");
    setSponsorshipLabel(c.sponsorshipLabel || DEFAULT_SPONSORSHIP_LABEL);
    setSiteCode(c.siteCode ?? sites[0]?.code ?? "herat-firdoosi-01");
    setTargetScope((c.scope as TargetScope) ?? "site");
    setGroupId(c.groupId ?? "");
    setPriority(c.priority ?? 0);
    setCtaType(c.ctaType ?? "whatsapp");
    setCtaLabel(c.ctaLabel);
    setCustomApprovedUrl(c.ctaType === "custom_https" ? (c.ctaHref ?? "") : "");
    setImagePath(c.imagePath);
    setImageAlt(c.imageAlt ?? "");
    setImagePreviewUrl(c.imagePath || null);
    const schedule = localScheduleFromIso(c.startsAt, c.endsAt);
    setStartsAtLocal(schedule.startsAtLocal);
    setEndsAtLocal(schedule.endsAtLocal);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [siteList, groupList, urls, entitlements] = await Promise.all([
        api.listSites(),
        api.listSiteGroups(),
        api.listApprovedCtaUrls(),
        api.getCurrentEntitlements().catch(() => null),
      ]);
      setSites(siteList);
      setGroups(groupList);
      setApprovedUrls(urls);
      const allowed = entitlements?.features.sponsored !== false;
      setSponsoredAllowed(allowed);
      setSiteCode((current) =>
        siteList.length > 0 && !siteList.some((s) => s.code === current)
          ? siteList[0]!.code
          : current,
      );
      if (!allowed) {
        setAdvertisers([]);
        setCampaigns([]);
        return;
      }
      const [advertiserList, campaignList] = await Promise.all([
        api.listAdvertisers(),
        api.listSponsoredCampaigns(),
      ]);
      setAdvertisers(advertiserList);
      setCampaigns(campaignList as Campaign[]);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setSponsoredAllowed(false);
        setAdvertisers([]);
        setCampaigns([]);
      } else {
        setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!advertiserId && activeAdvertisers[0]) {
      setAdvertiserId(activeAdvertisers[0].id);
    }
  }, [advertiserId, activeAdvertisers]);

  async function handleCreateAdvertiser(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSavingAdvertiser(true);
    setError(null);
    setSuccess(null);
    try {
      await api.createAdvertiser({
        displayName: advertiserName.trim(),
        internalNotes: advertiserNotes.trim() || null,
      });
      setAdvertiserName("");
      setAdvertiserNotes("");
      setSuccess(cmsStrings.sponsoredComposer.advertiserSaved);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ثبت تبلیغ‌کننده ناموفق");
    } finally {
      setSavingAdvertiser(false);
    }
  }

  async function handleArchiveAdvertiser(id: string, displayName: string) {
    if (!canEdit) return;
    setError(null);
    try {
      await api.archiveAdvertiser(id);
      setSuccess(`«${displayName}» بایگانی شد.`);
      if (advertiserId === id) setAdvertiserId("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بایگانی تبلیغ‌کننده ناموفق");
    }
  }

  async function handleImageSelect(file: File | null) {
    if (!file || !canEdit || !sponsoredAllowed) return;
    setUploadingImage(true);
    setError(null);
    try {
      const result = await api.uploadSponsoredImage(file);
      setImagePath(result.path);
      setImagePreviewUrl(result.path);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری تصویر ناموفق");
    } finally {
      setUploadingImage(false);
    }
  }

  function clearImage() {
    setImagePath("");
    setImageAlt("");
    setImagePreviewUrl(null);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    const schedule = schedulePayloadFromLocal(startsAtLocal, endsAtLocal);
    if (!schedule.startsAt || !schedule.endsAt) {
      setError(cmsStrings.sponsoredComposer.needSchedule);
      setSaving(false);
      return;
    }
    if (!imagePath) {
      setError(cmsStrings.sponsoredComposer.needImage);
      setSaving(false);
      return;
    }
    if (!advertiserId) {
      setError(cmsStrings.sponsoredComposer.needAdvertiser);
      setSaving(false);
      return;
    }

    const payload = {
      advertiserId,
      name,
      headline,
      description,
      offerText: offerText.trim() || null,
      sponsorshipLabel: sponsorshipLabel.trim() || DEFAULT_SPONSORSHIP_LABEL,
      imagePath,
      imageAlt,
      ctaType,
      ctaLabel,
      customApprovedUrl: ctaType === "custom_https" ? customApprovedUrl : undefined,
      ...targetingPayload(targetScope, siteCode, groupId, priority),
      startsAt: schedule.startsAt,
      endsAt: schedule.endsAt,
    };

    try {
      if (editingId) {
        await api.updateSponsoredCampaign(editingId, payload);
        setSuccess(cmsStrings.sponsoredComposer.draftUpdated);
      } else {
        await api.createSponsoredCampaign(payload);
        setSuccess(cmsStrings.sponsoredComposer.draftSaved);
        resetForm();
      }
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ذخیره ناموفق");
    } finally {
      setSaving(false);
    }
  }

  async function handlePasswordAction(e: FormEvent) {
    e.preventDefault();
    if (!passwordModal) return;
    setActionPending(true);
    setError(null);
    try {
      const { mode, campaign: c } = passwordModal;
      if (mode === "publish") {
        await api.publishSponsoredCampaign(c.id, password);
        setSuccess(`«${c.name}» منتشر شد.`);
        resetForm();
      } else if (mode === "archive") {
        await api.archiveSponsoredCampaign(c.id, password);
        setSuccess(`«${c.name}» بایگانی شد.`);
      } else if (mode === "pause") {
        await api.pauseSponsoredCampaign(c.id, password);
        setSuccess(`«${c.name}» متوقف شد.`);
      } else {
        await api.unpauseSponsoredCampaign(c.id, password);
        setSuccess(`«${c.name}» از توقف خارج شد.`);
      }
      setPasswordModal(null);
      setPassword("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "عملیات ناموفق");
    } finally {
      setActionPending(false);
    }
  }

  async function handleSubmit(c: Campaign) {
    setError(null);
    try {
      await api.submitSponsoredCampaign(c.id);
      setSuccess(`«${c.name}» برای تأیید ارسال شد.`);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ارسال برای تأیید ناموفق");
    }
  }

  async function handleReject(e: FormEvent) {
    e.preventDefault();
    if (!rejectModal) return;
    setRejecting(true);
    setError(null);
    try {
      await api.rejectSponsoredCampaign(rejectModal.id, rejectReason.trim());
      setSuccess(`«${rejectModal.name}» رد شد.`);
      setRejectModal(null);
      setRejectReason("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "رد کردن ناموفق");
    } finally {
      setRejecting(false);
    }
  }

  async function openHistory(c: Campaign) {
    setHistoryTarget(c);
    setHistoryLoading(true);
    setError(null);
    try {
      const list = await api.listSponsoredSnapshots(c.id);
      setSnapshots(list);
    } catch (err) {
      setHistoryTarget(null);
      setError(err instanceof ApiError ? err.message : "بارگذاری تاریخچه ناموفق");
    } finally {
      setHistoryLoading(false);
    }
  }

  async function handleRollback(e: FormEvent) {
    e.preventDefault();
    if (!historyTarget || !rollbackTarget) return;
    setRollbackBusy(true);
    setError(null);
    try {
      await api.rollbackSponsoredCampaign(historyTarget.id, rollbackTarget.id, rollbackPassword);
      setSuccess(`بازگشت به نسخه ${rollbackTarget.version} انجام شد (نسخهٔ جدید ثبت شد).`);
      setRollbackTarget(null);
      setRollbackPassword("");
      const list = await api.listSponsoredSnapshots(historyTarget.id);
      setSnapshots(list);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بازگشت ناموفق");
    } finally {
      setRollbackBusy(false);
    }
  }

  const filtered = useMemo(
    () =>
      filterAndSortSponsoredCampaigns(campaigns, {
        query: listQuery,
        listFilter,
        sort: listSort,
      }),
    [campaigns, listFilter, listQuery, listSort],
  );

  const queueCount = campaigns.filter((c) => c.state === "pending_approval").length;
  const pausedCount = campaigns.filter((c) => c.state === "paused").length;
  const publishedCount = campaigns.filter(
    (c) => c.state === "published" || c.state === "scheduled",
  ).length;
  const selectedAdvertiser = advertisers.find((a) => a.id === advertiserId);
  const sponsorshipLabelOptions = useMemo(
    () => sponsoredSponsorshipLabelOptions(sponsorshipLabel),
    [sponsorshipLabel],
  );

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.id === selectedId) ?? null,
    [campaigns, selectedId],
  );

  useEffect(() => {
    if (loading) return;
    if (selectedId && !campaigns.some((c) => c.id === selectedId)) {
      setSelectedId(null);
    }
  }, [campaigns, loading, selectedId, setSelectedId]);

  useEffect(() => {
    if (!editingId) return;
    document.getElementById("sponsored-composer")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [editingId]);

  useEffect(() => {
    if (!selectedId) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (passwordModal || rejectModal || historyTarget || rollbackTarget) return;
      e.preventDefault();
      setSelectedId(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedId, passwordModal, rejectModal, historyTarget, rollbackTarget]);

  const c = cmsStrings.sponsoredComposer;
  const passwordTitles: Record<PasswordMode, string> = {
    publish: c.publishConfirmTitle,
    archive: c.archiveConfirmTitle,
    pause: c.pauseConfirmTitle,
    unpause: c.resumeConfirmTitle,
  };
  const passwordLabels: Record<PasswordMode, string> = {
    publish: c.publishConfirmLabel,
    archive: c.archiveConfirmLabel,
    pause: c.pauseConfirmLabel,
    unpause: c.resumeConfirmLabel,
  };
  const passwordImpacts: Record<PasswordMode, string> = {
    publish: c.publishImpact,
    archive: c.archiveImpact,
    pause: c.pauseImpact,
    unpause: c.resumeImpact,
  };
  const imageMaxKb = Math.round(SPONSORED_IMAGE_MAX_BYTES / 1024);
  const imageAccept = PROMOTION_IMAGE_MIME_TYPES.join(",");

  function renderSponsoredInspectFooter(campaign: Campaign) {
    const tools: ReactNode[] = [];
    const primary: ReactNode[] = [];

    if (campaign.state === "draft") {
      tools.push(
        <button
          key="edit"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            setSelectedId(campaign.id);
            loadFormFromCampaign(campaign);
          }}
        >
          {c.edit}
        </button>,
      );
      if (canEdit && sponsoredAllowed) {
        tools.push(
          <button
            key="submit"
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void handleSubmit(campaign)}
          >
            {c.submitForApproval}
          </button>,
        );
      }
      if (canPublish && sponsoredAllowed) {
        primary.push(
          <button
            key="publish"
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => {
              setPasswordModal({ mode: "publish", campaign });
              setPassword("");
            }}
          >
            {c.publish}
          </button>,
        );
      }
    } else if (campaign.state === "pending_approval" && canPublish && sponsoredAllowed) {
      tools.push(
        <button
          key="reject"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            setRejectModal(campaign);
            setRejectReason("");
          }}
        >
          {c.reject}
        </button>,
      );
      primary.push(
        <button
          key="approve"
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => {
            setPasswordModal({ mode: "publish", campaign });
            setPassword("");
          }}
        >
          {c.approveAndPublish}
        </button>,
      );
    } else if (campaign.state === "published" || campaign.state === "scheduled") {
      tools.push(
        <button
          key="history"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void openHistory(campaign)}
        >
          {c.history}
        </button>,
      );
      if (canPublish && sponsoredAllowed) {
        tools.push(
          <button
            key="pause"
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setPasswordModal({ mode: "pause", campaign });
              setPassword("");
            }}
          >
            {c.pause}
          </button>,
          <button
            key="archive"
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setPasswordModal({ mode: "archive", campaign });
              setPassword("");
            }}
          >
            {c.archive}
          </button>,
        );
      }
    } else if (campaign.state === "paused" && canPublish && sponsoredAllowed) {
      primary.push(
        <button
          key="resume"
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => {
            setPasswordModal({ mode: "unpause", campaign });
            setPassword("");
          }}
        >
          {c.resume}
        </button>,
      );
    } else if (campaign.state === "archived" || campaign.state === "expired") {
      tools.push(
        <button
          key="history"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void openHistory(campaign)}
        >
          {c.history}
        </button>,
      );
    }

    return (
      <div className="inspect-panel__footer-inner">
        {tools.length > 0 ? <div className="inspect-panel__footer-tools">{tools}</div> : null}
        {primary.length > 0 ? <div className="inspect-panel__footer-primary">{primary}</div> : null}
      </div>
    );
  }

  function renderCampaignActions(campaign: Campaign) {
    return (
      <>
        {campaign.state === "draft" && (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setSelectedId(campaign.id);
                loadFormFromCampaign(campaign);
              }}
            >
              {c.edit}
            </button>
            {canEdit && sponsoredAllowed && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => void handleSubmit(campaign)}
              >
                {c.submitForApproval}
              </button>
            )}
            {canPublish && sponsoredAllowed && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => {
                  setPasswordModal({ mode: "publish", campaign });
                  setPassword("");
                }}
              >
                {c.publish}
              </button>
            )}
          </>
        )}
        {campaign.state === "pending_approval" && canPublish && sponsoredAllowed && (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setRejectModal(campaign);
                setRejectReason("");
              }}
            >
              {c.reject}
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => {
                setPasswordModal({ mode: "publish", campaign });
                setPassword("");
              }}
            >
              {c.approveAndPublish}
            </button>
          </>
        )}
        {(campaign.state === "published" || campaign.state === "scheduled") && (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => void openHistory(campaign)}
            >
              {c.history}
            </button>
            {canPublish && sponsoredAllowed && (
              <>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    setPasswordModal({ mode: "pause", campaign });
                    setPassword("");
                  }}
                >
                  {c.pause}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    setPasswordModal({ mode: "archive", campaign });
                    setPassword("");
                  }}
                >
                  {c.archive}
                </button>
              </>
            )}
          </>
        )}
        {campaign.state === "paused" && canPublish && sponsoredAllowed && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => {
              setPasswordModal({ mode: "unpause", campaign });
              setPassword("");
            }}
          >
            {c.resume}
          </button>
        )}
        {(campaign.state === "archived" || campaign.state === "expired") && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void openHistory(campaign)}
          >
            {c.history}
          </button>
        )}
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={cmsStrings.nav.sponsored}
        actions={
          canEdit && sponsoredAllowed ? (
            <a href="#sponsored-composer" className="btn btn--primary btn--sm">
              {c.createDraftCta}
            </a>
          ) : undefined
        }
      />

      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="banner banner--success" role="status">
          {success}
        </p>
      )}
      {!sponsoredAllowed && (
        <p className="banner banner--warning" role="status">
          {c.entitlementDenied}
        </p>
      )}

      <div className="metric-strip" data-vt-inventory-metrics aria-label={cmsStrings.sponsoredUi.listTitle}>
        <MetricCard
          label={cmsStrings.inventory.filterAll}
          value={campaigns.length}
          loading={loading}
        />
        <MetricCard
          label={cmsStrings.inventory.filterQueue}
          value={queueCount}
          loading={loading}
          tone={queueCount > 0 ? "warning" : "neutral"}
        />
        <MetricCard
          label={cmsStrings.overviewUi.publishedOrScheduled}
          value={publishedCount}
          loading={loading}
          sublabel={
            pausedCount > 0
              ? `${cmsStrings.inventory.filterPaused}: ${pausedCount}`
              : undefined
          }
        />
      </div>

      <div className="page-stack page-stack--inventory">
      <div
        className={`content-ops-layout${selectedCampaign ? " content-ops-layout--inspect-open" : ""}`}
        data-vt-content-ops
        data-vt-inspect-open={selectedCampaign ? "true" : "false"}
      >
        <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
          <SectionHeader title={cmsStrings.sponsoredUi.listTitle} />
          <SponsoredFilterBar
            listFilter={listFilter}
            onListFilterChange={setListFilter}
            queueCount={queueCount}
            query={listQuery}
            onQueryChange={setListQuery}
            sort={listSort}
            onSortChange={setListSort}
          />
          {loading ? (
            <div className="inventory-skeleton" aria-busy="true" aria-label={cmsStrings.shell.loading}>
              <div className="inventory-skeleton__row" style={{ width: "92%" }} />
              <div className="inventory-skeleton__row" style={{ width: "78%" }} />
              <div className="inventory-skeleton__row" style={{ width: "85%" }} />
              <div className="inventory-skeleton__row" style={{ width: "70%" }} />
              <div className="inventory-skeleton__row" style={{ width: "88%" }} />
            </div>
          ) : campaigns.length === 0 ? (
            <OpsEmptyState title={cmsStrings.sponsoredUi.emptyTitle} />
          ) : filtered.length === 0 ? (
            <OpsEmptyState title={cmsStrings.inventory.noMatchTitle} />
          ) : (
            <SponsoredInventoryList
              rows={filtered}
              selectedId={selectedId}
              onSelect={(id) => setSelectedId((current) => (current === id ? null : id))}
              renderActions={(row) => renderCampaignActions(row as Campaign)}
            />
          )}
        </Card>

        {selectedCampaign && (
          <InspectPanel
            title={cmsStrings.sponsoredUi.inspectTitle}
            className="content-inspect-panel"
            onClose={() => setSelectedId(null)}
            closeLabel={c.close}
            footer={renderSponsoredInspectFooter(selectedCampaign)}
          >
            <SponsoredInspectSummary item={selectedCampaign} sponsoredAllowed={sponsoredAllowed} />
          </InspectPanel>
        )}
      </div>

      <div className="sponsored-ops-stack">
        <ComposerPanel className="composer-panel--advertiser sponsored-advertiser-panel">
          <ComposerHeader
            kind={cmsStrings.composerChrome.kindAdvertiser}
            title={c.advertisersTitle}
            statusLabel={cmsStrings.composerChrome.sectionAdvertiser}
            statusTone="neutral"
          />
          {canEdit && sponsoredAllowed && (
            <form
              className="content-form content-form--in-card composer-form composer-form--advertiser"
              onSubmit={handleCreateAdvertiser}
            >
              <ComposerSection
                id="sponsored-section-advertiser"
                title={cmsStrings.composerChrome.sectionAdvertiser}
                bare
              >
                <div className="composer-fields-row">
                  <label>
                    <span className="composer-label">
                      {c.advertiserName}
                      <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                        {advertiserName.length}/{ADVERTISER_NAME_MAX}
                      </bdi>
                    </span>
                    <input
                      value={advertiserName}
                      onChange={(e) => setAdvertiserName(e.target.value.slice(0, ADVERTISER_NAME_MAX))}
                      required
                      maxLength={ADVERTISER_NAME_MAX}
                    />
                  </label>
                  <label>
                    <span className="composer-label">
                      {c.advertiserNotes}
                      <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                        {advertiserNotes.length}/{NOTES_MAX}
                      </bdi>
                    </span>
                    <textarea
                      value={advertiserNotes}
                      onChange={(e) => setAdvertiserNotes(e.target.value.slice(0, NOTES_MAX))}
                      rows={2}
                      maxLength={NOTES_MAX}
                    />
                  </label>
                </div>
              </ComposerSection>
              <StickyFormActions>
                <button type="submit" className="btn btn--primary btn--sm" disabled={savingAdvertiser}>
                  {savingAdvertiser ? c.saving : c.advertiserSave}
                </button>
              </StickyFormActions>
            </form>
          )}
          {loading ? (
            <OpsEmptyState title={cmsStrings.shell.loading} />
          ) : advertisers.length === 0 ? (
            <OpsEmptyState title={cmsStrings.sponsoredUi.emptyAdvertisersTitle} />
          ) : (
            <SponsoredAdvertiserList
              rows={advertisers}
              canArchive={canEdit && sponsoredAllowed}
              onArchive={(id, displayName) => void handleArchiveAdvertiser(id, displayName)}
              statusActiveLabel={c.advertiserStatusActive}
              statusArchivedLabel={c.advertiserStatusArchived}
              archiveLabel={c.advertiserArchive}
              notesPrefix={c.advertiserNotesPrefix}
            />
          )}
        </ComposerPanel>

        {canEdit && sponsoredAllowed && (
        <ComposerWorkspace id="sponsored-composer">
          <ComposerPanel>
            <ComposerHeader
              kind={cmsStrings.composerChrome.kindSponsored}
              title={editingId ? c.cardTitleEdit : c.cardTitleCreate}
              statusLabel={
                editingId
                  ? cmsStrings.composerChrome.statusEditDraft
                  : cmsStrings.composerChrome.statusCreate
              }
              statusTone={editingId ? "warning" : "info"}
            />
            <form
              className="content-form content-form--in-card composer-form composer-form--sponsored"
              onSubmit={handleSave}
            >
              <ComposerSection id="sponsored-section-basic" title={cmsStrings.composerChrome.sectionBasic} bare>
                <label>
                  <span>{c.advertiserLabel}</span>
                  <select
                    value={advertiserId}
                    onChange={(e) => setAdvertiserId(e.target.value)}
                    required
                  >
                    <option value="">{c.selectPlaceholder}</option>
                    {activeAdvertisers.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.displayName}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="composer-fields-row composer-fields-row--title-badge">
                  <label className="composer-field--grow">
                    <span className="composer-label">
                      {c.campaignName}
                      <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                        {name.length}/{NAME_MAX}
                      </bdi>
                    </span>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))}
                      required
                      maxLength={NAME_MAX}
                    />
                  </label>
                  <label className="composer-field--badge">
                    <span>{c.sponsorshipLabel}</span>
                    <select
                      value={sponsorshipLabel}
                      onChange={(e) => setSponsorshipLabel(e.target.value.slice(0, LABEL_MAX))}
                      required
                    >
                      {sponsorshipLabelOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <label>
                  <span className="composer-label">
                    {c.headline}
                    <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                      {headline.length}/{HEADLINE_MAX}
                    </bdi>
                  </span>
                  <input
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value.slice(0, HEADLINE_MAX))}
                    required
                    maxLength={HEADLINE_MAX}
                  />
                </label>
                <label>
                  <span className="composer-label">
                    {c.description}
                    <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                      {description.length}/{DESCRIPTION_MAX}
                    </bdi>
                  </span>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
                    required
                    rows={3}
                    maxLength={DESCRIPTION_MAX}
                  />
                </label>
                <label>
                  <span className="composer-label">
                    {c.offerText} — {c.optionalSuffix}
                    <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                      {offerText.length}/{OFFER_MAX}
                    </bdi>
                  </span>
                  <input
                    value={offerText}
                    onChange={(e) => setOfferText(e.target.value.slice(0, OFFER_MAX))}
                    maxLength={OFFER_MAX}
                  />
                </label>
              </ComposerSection>
              <ComposerSection
                id="sponsored-section-targeting"
                title={cmsStrings.composerChrome.sectionTargeting}
                bare
              >
                <TargetFields
                  compact
                  sites={sites}
                  groups={groups}
                  targetScope={targetScope}
                  siteCode={siteCode}
                  groupId={groupId}
                  priority={priority}
                  onTargetScopeChange={setTargetScope}
                  onSiteCodeChange={setSiteCode}
                  onGroupIdChange={setGroupId}
                  onPriorityChange={setPriority}
                  labels={{
                    ...cmsStrings.contentTargeting,
                    globalHint: "",
                    groupEmptyHint: "",
                  }}
                />
              </ComposerSection>
              <ComposerSection
                id="sponsored-section-cta"
                title={cmsStrings.composerChrome.sectionCtaImage}
                bare
              >
                <div className="composer-fields-row">
                  <label>
                    <span>
                      {c.ctaDestination} — {c.ctaRequiredSuffix}
                    </span>
                    <select
                      value={ctaType}
                      required
                      onChange={(e) => {
                        const v = e.target.value as CtaType;
                        setCtaType(v);
                        if (v !== "custom_https") setCustomApprovedUrl("");
                      }}
                    >
                      {CTA_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {CTA_TYPE_LABELS[t]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span className="composer-label">
                      {c.ctaButtonLabel}
                      <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                        {ctaLabel.length}/{CONTENT_LIMITS.ctaLabelMax}
                      </bdi>
                    </span>
                    <input
                      value={ctaLabel}
                      onChange={(e) => setCtaLabel(e.target.value.slice(0, CONTENT_LIMITS.ctaLabelMax))}
                      required
                      maxLength={CONTENT_LIMITS.ctaLabelMax}
                    />
                  </label>
                </div>
                {ctaType === "custom_https" && (
                  <label>
                    <span>{c.ctaApprovedUrl}</span>
                    <select
                      value={customApprovedUrl}
                      onChange={(e) => setCustomApprovedUrl(e.target.value)}
                      required
                      dir="ltr"
                      className="ltr-isolate"
                    >
                      <option value="">{c.selectPlaceholder}</option>
                      {approvedUrls.map((url) => (
                        <option key={url} value={url}>
                          {url}
                        </option>
                      ))}
                    </select>
                    {approvedUrls.length === 0 && (
                      <p className="field-hint">{c.ctaAllowlistEmpty}</p>
                    )}
                  </label>
                )}
                <div className="composer-image-block">
                  {imagePreviewUrl ? (
                    <div className="composer-image-preview">
                      <img
                        src={imagePreviewUrl}
                        alt={imageAlt || headline || c.previewEmptyHeadline}
                        className="composer-image-preview__thumb"
                      />
                      <div className="composer-image-preview__meta">
                        <span className="composer-image-preview__name ltr-isolate" dir="ltr">
                          {sponsoredCreativeFilename(imagePath) || c.imagePick}
                        </span>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={clearImage}>
                          {c.imageClear}
                        </button>
                      </div>
                    </div>
                  ) : null}
                  <label>
                    <span>
                      {c.imageLabelBefore}{" "}
                      <bdi dir="ltr" className="ltr-isolate latin-digits">
                        {imageMaxKb}
                      </bdi>{" "}
                      {c.imageLabelMid}{" "}
                      <bdi dir="ltr" className="ltr-isolate latin-digits">
                        {SPONSORED_IMAGE_MAX_EDGE_PX}
                      </bdi>
                      {c.imageLabelAfter}
                    </span>
                    <span className={`file-drop-hint${imagePreviewUrl ? " file-drop-hint--compact" : ""}`}>
                      {uploadingImage ? c.imageUploading : c.imagePick}
                      <input
                        type="file"
                        accept={imageAccept}
                        disabled={uploadingImage}
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          void handleImageSelect(file);
                          e.target.value = "";
                        }}
                      />
                    </span>
                  </label>
                  {imagePath ? (
                    <label>
                      <span className="composer-label">
                        {c.imageAltLabel}
                        <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                          {imageAlt.length}/{CONTENT_LIMITS.imageAltMax}
                        </bdi>
                      </span>
                      <input
                        value={imageAlt}
                        onChange={(e) => setImageAlt(e.target.value.slice(0, CONTENT_LIMITS.imageAltMax))}
                        required
                        maxLength={CONTENT_LIMITS.imageAltMax}
                      />
                    </label>
                  ) : null}
                </div>
              </ComposerSection>
              <ComposerSection
                id="sponsored-section-schedule"
                title={cmsStrings.composerChrome.sectionSchedule}
                bare
              >
                <ScheduleFields
                  compact
                  required
                  startsAtLocal={startsAtLocal}
                  endsAtLocal={endsAtLocal}
                  onStartsAtChange={setStartsAtLocal}
                  onEndsAtChange={setEndsAtLocal}
                  legend={c.scheduleLegend}
                  hint={null}
                />
              </ComposerSection>
              <StickyFormActions>
                <button type="submit" className="btn btn--primary" disabled={saving}>
                  {saving ? c.saving : editingId ? c.updateDraft : c.saveDraft}
                </button>
                {editingId && (
                  <button type="button" className="btn btn--ghost" onClick={resetForm}>
                    {c.cancel}
                  </button>
                )}
              </StickyFormActions>
            </form>
          </ComposerPanel>

          <Card
            title={c.previewTitle}
            className="preview-panel"
            bodyClassName="card__body--flush"
          >
            <SponsoredPreview
              advertiserName={selectedAdvertiser?.displayName ?? ""}
              sponsorshipLabel={sponsorshipLabel}
              headline={headline}
              description={description}
              offerText={offerText}
              ctaLabel={ctaLabel}
              ctaType={ctaType}
              imageUrl={imagePreviewUrl}
              imageAlt={imageAlt}
            />
          </Card>
        </ComposerWorkspace>
        )}
      </div>

      </div>

      {passwordModal && (
        <PasswordConfirmModal
          title={passwordTitles[passwordModal.mode]}
          description={passwordImpacts[passwordModal.mode]}
          summary={
            <dl className="publish-summary">
              <dt>{c.summaryCampaign}</dt>
              <dd>{passwordModal.campaign.name}</dd>
              <dt>{c.summaryAdvertiser}</dt>
              <dd>{passwordModal.campaign.advertiserName}</dd>
              <dt>{c.summaryTarget}</dt>
              <dd>{formatTargetLabel(passwordModal.campaign)}</dd>
              <dt>{c.summarySchedule}</dt>
              <dd>
                <bdi dir="ltr" className="ltr-isolate">
                  {formatKabulDisplay(passwordModal.campaign.startsAt)} –{" "}
                  {formatKabulDisplay(passwordModal.campaign.endsAt)}
                </bdi>
              </dd>
              <dt>{c.summaryCta}</dt>
              <dd>
                {sponsoredCtaLabelFa(passwordModal.campaign.ctaType, passwordModal.campaign.ctaLabel)}
              </dd>
            </dl>
          }
          password={password}
          onPasswordChange={setPassword}
          confirming={actionPending}
          confirmLabel={passwordLabels[passwordModal.mode]}
          tone={
            passwordModal.mode === "archive" || passwordModal.mode === "pause"
              ? "danger"
              : "primary"
          }
          onCancel={() => setPasswordModal(null)}
          onConfirm={handlePasswordAction}
        />
      )}

      {rejectModal && (
        <div className="modal-backdrop" role="presentation" onClick={() => setRejectModal(null)}>
          <div className="modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h2>{c.rejectDialogTitle}</h2>
            <p>
              «{rejectModal.name}» {c.rejectDialogBody}
            </p>
            <form onSubmit={handleReject}>
              <label>
                <span>{c.rejectReason}</span>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value.slice(0, 500))}
                  required
                  rows={3}
                  maxLength={500}
                />
              </label>
              <div className="modal-actions">
                <button type="button" className="btn btn--ghost" onClick={() => setRejectModal(null)}>
                  {c.cancel}
                </button>
                <button type="submit" className="btn btn--danger" disabled={rejecting} aria-busy={rejecting}>
                  {rejecting ? c.rejecting : c.rejectConfirm}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {historyTarget && (
        <div className="modal-backdrop" role="presentation" onClick={() => setHistoryTarget(null)}>
          <div className="modal modal--wide" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h2>{c.historyTitle}</h2>
            <p>{historyTarget.name}</p>
            {historyLoading ? (
              <p>{c.loadingInline}</p>
            ) : snapshots.length === 0 ? (
              <p>{c.historyEmpty}</p>
            ) : (
              <ul className="snapshot-list">
                {snapshots.map((s) => (
                  <li key={s.id}>
                    <div>
                      <strong>
                        {c.historyVersion}{" "}
                        <bdi dir="ltr" className="ltr-isolate">
                          {s.version}
                        </bdi>
                      </strong>
                      {s.rollbackOfSnapshotId && (
                        <span className="state-badge state-badge--scheduled">{c.rollbackBadge}</span>
                      )}
                      <span className="ltr-isolate" dir="ltr">
                        {s.publishedAt}
                      </span>
                      <code className="ltr-isolate" dir="ltr">
                        {s.payloadChecksum.slice(0, 16)}…
                      </code>
                    </div>
                    {canPublish && sponsoredAllowed && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => {
                          setRollbackTarget(s);
                          setRollbackPassword("");
                        }}
                      >
                        {c.rollbackAction}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <div className="modal-actions">
              <button type="button" className="btn btn--ghost" onClick={() => setHistoryTarget(null)}>
                {c.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {rollbackTarget && (
        <PasswordConfirmModal
          title={`${c.rollbackConfirmTitleBefore} ${rollbackTarget.version}`}
          description={c.rollbackImpact}
          password={rollbackPassword}
          onPasswordChange={setRollbackPassword}
          confirming={rollbackBusy}
          confirmLabel={c.rollbackConfirmLabel}
          tone="danger"
          onCancel={() => setRollbackTarget(null)}
          onConfirm={handleRollback}
        />
      )}
    </>
  );
}
